import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, Search, X } from 'lucide-react';
import { resolveImageUrl } from '../../lib/utils';
import api from '../../lib/api';
import { useMills } from '../../context/MillsContext';

/* Six mega menu layouts. Each receives the live catalogue plus the admin-managed
   quick links, so adding a grade keeps working whichever template is selected.
   Rule for all six: the whole catalogue has to be visible without scrolling. */

/* Grows to fit the tallest column so nothing ever has to scroll. */
const PANEL = 'lg:min-h-[460px]';

// grade families count grades; the industrial products group counts product lines
const countLabel = (group, t) =>
  `${group.items.length} ${group.kind === 'product' ? t('products.statProducts') : t('common.gradesCount')}`;

/* Grades that belong to different product standards (Cr-Mo plate vs pipe) keep their own pages
   but read as one family in the menu, split by the sub-heading the API sends on each item. */
const grouped = (items) => {
  const out = [];
  items.forEach(it => {
    const label = it.group || '';
    const last = out[out.length - 1];
    if (last && last.label === label) last.items.push(it);
    else out.push({ label, items: [it] });
  });
  return out;
};

const GroupHead = ({ label }) => (
  <p className="px-data pt-3 pb-1.5 text-[9px] uppercase tracking-[0.22em] text-[var(--px-accent)]">{label}</p>
);

const Quick = ({ links, lang, inline = false }) => (
  <div className={inline ? 'flex flex-wrap gap-x-8 gap-y-2.5' : 'space-y-2.5'}>
    {links.filter(l => l.visible !== false).map(l => (
      <Link
        key={l.id || l.url}
        to={l.url}
        data-testid={`mega-quick-${l.url}`}
        className="px-data block text-[10px] tracking-[0.18em] uppercase text-[var(--px-ink-soft)] hover:text-[var(--px-accent)] transition-colors"
      >
        {lang === 'ar' && l.label_ar ? l.label_ar : l.label}
      </Link>
    ))}
  </div>
);

/* Standing proof of who we buy from — one compact line, never a scrollable column. */
const MillStrip = ({ t }) => {
  const { principals, cfg } = useMills();
  if (cfg?.mega_menu === false || !principals.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5" data-testid="mega-mill-strip">
      <span className="px-data text-[9px] uppercase tracking-[0.25em] text-[var(--px-ink-soft)]">
        {cfg?.heading || t('mills.kicker')}
      </span>
      {principals.slice(0, 5).map(m => (
        <Link
          key={m.id || m.name}
          to="/mills"
          data-testid={`mega-mill-${(m.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          className="px-display text-[12px] font-semibold text-[var(--px-ink)] hover:text-[var(--px-accent)] transition-colors"
        >
          {m.name}
        </Link>
      ))}
      <Link to="/mills" data-testid="mega-mill-all"
        className="px-data text-[9px] uppercase tracking-[0.18em] text-[var(--px-ink-soft)] hover:text-[var(--px-accent)] transition-colors">
        {t('mills.seeAll')}
      </Link>
    </div>
  );
};

const QuickBar = ({ links, lang, t }) => (
  <div className="border-t px-hair px-8 py-3 bg-[var(--px-paper)] space-y-2">
    <Quick links={links} lang={lang} inline />
    {t && <MillStrip t={t} />}
  </div>
);

/* Ex-stock marker. Grades on mill indent get no dot, so the dot always means "available now". */
const StockDot = ({ on, label, bright = false }) =>
  on ? (
    <span
      title={label || 'Ex-stock'}
      data-testid="stock-dot"
      className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${bright ? 'bg-emerald-400' : 'bg-emerald-500'}`}
    />
  ) : null;

const Thumb = ({ src, alt, className = '' }) => (
  <span className={`block overflow-hidden rounded-lg bg-[var(--px-paper)] border px-hair ${className}`}>
    <img src={resolveImageUrl(src)} alt={alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" />
  </span>
);

/* Grade search strip. Wraps whichever template is active: with an empty box it renders the
   template, while typing it filters the live catalogue (name, standard, equivalent, designation)
   and shows the matches in place — no page load, works on every template. */
export const MegaSearch = ({ cats, t, onNavigate, children, compact = false }) => {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const total = cats.filter(c => c.kind !== 'product').reduce((n, c) => n + c.items.length, 0);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    const out = [];
    cats.forEach(c => c.items.forEach(item => {
      const hay = `${item.keywords || ''} ${item.name} ${c.title}`.toLowerCase();
      if (hay.includes(term)) out.push({ ...item, family: c.title, familySlug: c.slug });
    }));
    return out.slice(0, 10);
  }, [q, cats]);

  // log the settled term (and any click-through) so the admin can see demand gaps
  const logged = useRef('');
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2 || logged.current === term.toLowerCase()) return;
    const id = setTimeout(() => {
      logged.current = term.toLowerCase();
      api.post('/analytics/search', { q: term, source: compact ? 'mega_mobile' : 'mega', results: results.length })
        .catch(() => {});
    }, 800);
    return () => clearTimeout(id);
  }, [q, results.length, compact]);

  const go = (r) => {
    const term = q.trim();
    if (term.length >= 2) {
      api.post('/analytics/search', { q: term, source: compact ? 'mega_mobile' : 'mega', results: results.length, clicked: r.slug })
        .catch(() => {});
    }
    setQ('');
    onNavigate?.();
    navigate(r.url || `/materials/${r.familySlug}/${r.slug}`);
  };

  const onKey = (e) => {
    if (e.key === 'Escape') { setQ(''); inputRef.current?.blur(); }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (results.length) go(results[0]);
      else if (q.trim()) { onNavigate?.(); navigate(`/grades?q=${encodeURIComponent(q.trim())}`); }
    }
  };

  return (
    <div>
      <div className={`flex items-center gap-3 border-b px-hair bg-[var(--px-paper)] ${compact ? 'px-6 py-2.5' : 'px-8 py-3'}`}>
        <Search className="w-4 h-4 text-[var(--px-ink-soft)] shrink-0" />
        <input
          ref={inputRef}
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={onKey}
          data-testid="mega-search-input"
          placeholder={`${t('nav.megaSearch')}`}
          className="flex-1 bg-transparent text-sm text-[var(--px-ink)] placeholder:text-[var(--px-ink-soft)] focus:outline-none py-1"
        />
        {q ? (
          <button onClick={() => setQ('')} data-testid="mega-search-clear" className="px-data text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink-soft)] hover:text-[var(--px-ink)] flex items-center gap-1.5">
            <X className="w-3 h-3" /> {results.length}
          </button>
        ) : (
          <span className="px-data hidden md:flex items-center gap-2 text-[9px] uppercase tracking-[0.2em] text-[var(--px-ink-soft)]">
            {total} {t('common.gradesCount')}
            <span className="flex items-center gap-1.5 ms-2">
              <StockDot on label={t('grade.stock')} /> {t('nav.exStock')}
            </span>
          </span>
        )}
      </div>

      {q.trim() ? (
        <div data-testid="mega-search-results" className={compact ? 'py-2' : 'p-4 md:p-6'}>
          {results.length === 0 ? (
            <p className="px-data px-4 py-8 text-center text-[10px] uppercase tracking-[0.2em] text-[var(--px-ink-soft)]">
              {t('nav.megaNoMatch')} “{q}”
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-1">
              {results.map(r => (
                <button
                  key={`${r.familySlug}-${r.slug}`}
                  onClick={() => go(r)}
                  data-testid={`mega-search-result-${r.slug}`}
                  className="group flex items-center gap-3.5 px-3 py-2 rounded-lg text-start hover:bg-[var(--px-paper)] transition-colors"
                >
                  {!compact && <Thumb src={r.img} alt={r.name} className="w-10 h-10 shrink-0" />}
                  <span className="min-w-0">
                    <span className="grade-name flex items-center gap-2 text-sm font-medium text-[var(--px-ink)]">
                      <span className="truncate">{r.name}</span>
                      <StockDot on={r.in_stock} label={r.stock} />
                    </span>
                    <span className="px-data block text-[9px] uppercase tracking-[0.18em] text-[var(--px-ink-soft)] mt-0.5 truncate">
                      {r.family}{r.stock ? ` · ${r.stock}` : ''}
                    </span>
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 ms-auto shrink-0 text-[var(--px-accent)] opacity-0 group-hover:opacity-100 transition-opacity rtl-flip" />
                </button>
              ))}
            </div>
          )}
          <div className="border-t px-hair mt-4 pt-4 px-3 flex items-center justify-between gap-4">
            <span className="px-data text-[9px] uppercase tracking-[0.2em] text-[var(--px-ink-soft)]">{t('nav.megaSearchHint')}</span>
            <Link
              to={`/grades?q=${encodeURIComponent(q.trim())}`}
              onClick={() => { setQ(''); onNavigate?.(); }}
              data-testid="mega-search-see-all"
              className="px-data inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[var(--px-accent)]"
            >
              {t('nav.megaSeeAll')} <ArrowRight className="w-3 h-3 rtl-flip" />
            </Link>
          </div>
        </div>
      ) : children}
    </div>
  );
};

/* 1 — Rail: family rail, grade grid, featured panel. Quick links sit in a bottom bar
   so the rail itself has room for every family without scrolling. */
export const MegaRail = ({ cats, cat, setCat, hovered, links, lang, t }) => (
  <div className="flex flex-col">
    <div className={`flex ${PANEL}`}>
      <div className="w-[260px] bg-[var(--px-paper)] border-e px-hair py-3 flex flex-col shrink-0">
        <p className="px-data px-6 pb-2 text-[9px] uppercase tracking-[0.3em] text-[var(--px-ink-soft)]">{t('nav.megaCategories')}</p>
        <div className="flex-1 flex flex-col justify-start">
          {cats.map((c, i) => (
            <button
              key={c.slug}
              onMouseEnter={() => setCat(i)}
              data-testid={`mega-cat-${i}`}
              className={`w-full text-start px-6 py-[7px] flex items-center justify-between gap-3 group transition-colors duration-300 ${hovered === i ? 'bg-[var(--px-surface)]' : 'hover:bg-[var(--px-surface)]/60'}`}
            >
              <span className={`px-display truncate text-[13px] font-semibold leading-tight ${hovered === i ? 'text-[var(--px-ink)]' : 'text-[var(--px-ink-soft)] group-hover:text-[var(--px-ink)]'}`}>
                {c.title}
              </span>
              <ChevronRight className={`w-3.5 h-3.5 shrink-0 rtl-flip ${hovered === i ? 'text-[var(--px-accent)] opacity-100' : 'opacity-0'}`} />
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 px-8 py-5 flex flex-col min-w-0">
        <div className="flex items-end justify-between gap-6 mb-2 border-b px-hair pb-3">
          <span className="min-w-0">
            <h4 className="px-data text-[9px] uppercase tracking-[0.3em] text-[var(--px-ink-soft)]">
              {cat.title} · {countLabel(cat, t)}
            </h4>
            {cat.desc && <p className="text-[12px] text-[var(--px-ink-soft)] mt-1.5 line-clamp-1">{cat.desc}</p>}
          </span>
          <Link to={cat.url || `/materials/${cat.slug}`} data-testid="mega-menu-browse-category" className="px-data shrink-0 text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink)] hover:text-[var(--px-accent)] flex items-center gap-1.5">
            {t('nav.browseAll')} <ArrowRight className="w-3 h-3 rtl-flip" />
          </Link>
        </div>
        {grouped(cat.items).map(g => (
          <div key={g.label || 'all'}>
            {g.label && <GroupHead label={g.label} />}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-6 gap-y-0.5 content-start">
              {g.items.map(item => (
                <Link key={item.slug} to={item.url || `/materials/${cat.slug}/${item.slug}`} className="group flex items-center gap-3 px-3 py-1 rounded-lg hover:bg-[var(--px-paper)] transition-colors duration-300">
                  <Thumb src={item.img} alt={item.name} className="w-8 h-8 shrink-0" />
                  <span className="grade-name flex items-center gap-2 min-w-0 text-[13px] font-medium text-[var(--px-ink-soft)] group-hover:text-[var(--px-ink)] transition-colors">
                    <span className="truncate">{item.short || item.name}</span>
                    <StockDot on={item.in_stock} label={item.stock} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="w-[250px] border-s px-hair p-6 hidden xl:flex flex-col">
        <p className="px-data text-[9px] uppercase tracking-[0.3em] text-[var(--px-ink-soft)] mb-4">{t('nav.featured')}</p>
        <Link to={cat.featured.url || `/materials/${cat.slug}/${cat.featured.slug}`} data-testid="mega-featured-product" className="group flex-1 flex flex-col rounded-lg overflow-hidden bg-[var(--px-paper)] border px-hair">
          <span className="relative flex-1 overflow-hidden">
            <img src={resolveImageUrl(cat.featured.img)} alt={cat.featured.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.08]" />
            <span className="px-data absolute top-3 left-3 text-[9px] uppercase tracking-[0.2em] bg-[var(--px-ink)] text-white px-2.5 py-1 rounded-full">{t('nav.mostAsked')}</span>
          </span>
          <span className="p-4 block">
            <span className="px-display block text-sm font-semibold text-[var(--px-ink)]">{cat.featured.name}</span>
            <span className="px-data flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink-soft)] mt-2 group-hover:text-[var(--px-accent)]">{t('nav.viewDatasheet')} <ArrowRight className="w-3 h-3 rtl-flip" /></span>
          </span>
        </Link>
      </div>
    </div>
    <QuickBar links={links} lang={lang} t={t} />
  </div>
);

/* 2 — Columns: every family and every grade on screen at once, no truncation */
export const MegaColumns = ({ cats, links, lang, t }) => (
  <div>
    <div className="p-8 md:p-10">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-x-8 gap-y-8">
        {cats.map((c, i) => (
          <div key={c.slug} data-testid={`mega-cat-${i}`}>
            <Link to={c.url || `/materials/${c.slug}`} className="px-data block text-[9px] uppercase tracking-[0.28em] text-[var(--px-ink-soft)] pb-2.5 mb-2.5 border-b px-hair hover:text-[var(--px-accent)] transition-colors">
              {c.title}
            </Link>
            <div className="space-y-1">
              {grouped(c.items).map(g => (
                <div key={g.label || 'all'}>
                  {g.label && <GroupHead label={g.label} />}
                  {g.items.map(item => (
                    <Link key={item.slug} to={item.url || `/materials/${c.slug}/${item.slug}`} className="grade-name flex items-center gap-2 text-sm text-[var(--px-ink-soft)] hover:text-[var(--px-accent)] transition-colors">
                      <span className="truncate">{item.short || item.name}</span>
                      <StockDot on={item.in_stock} label={item.stock} />
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
    <QuickBar links={links} lang={lang} t={t} />
  </div>
);

/* 3 — Cards: families as image cards, all nine visible */
export const MegaCards = ({ cats, links, lang, t }) => (
  <div>
    <div className="p-8 md:p-10">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {cats.map((c, i) => (
          <Link key={c.slug} to={c.url || `/materials/${c.slug}`} data-testid={`mega-cat-${i}`} className="group relative overflow-hidden rounded-lg aspect-[5/4] bg-[var(--px-ink)]">
            <img src={resolveImageUrl(c.featured?.img)} alt={c.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70 transition-all duration-700 group-hover:opacity-90 group-hover:scale-[1.06]" />
            <span className="absolute inset-0 px-scrim-base" />
            <span className="absolute inset-x-0 bottom-0 p-4">
              <span className="px-display block text-sm font-semibold text-white leading-tight">{c.title}</span>
              <span className="px-data block text-[9px] tracking-[0.18em] uppercase text-white/55 mt-1.5">
                {countLabel(c, t)}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </div>
    <QuickBar links={links} lang={lang} t={t} />
  </div>
);

/* 4 — Editorial: families listed large beside one image */
export const MegaEditorial = ({ cats, cat, setCat, hovered, links, lang, t }) => (
  <div className="flex flex-col">
    <div className={`grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] ${PANEL}`}>
      <div className="p-8 md:p-10 flex flex-col">
        <p className="px-data text-[9px] uppercase tracking-[0.3em] text-[var(--px-ink-soft)] mb-4">{t('nav.megaCategories')}</p>
        <div className="divide-y px-hair border-y px-hair">
          {cats.map((c, i) => (
            <button
              key={c.slug}
              onMouseEnter={() => setCat(i)}
              onClick={() => { window.location.href = c.url || `/materials/${c.slug}`; }}
              data-testid={`mega-cat-${i}`}
              className="w-full text-start py-2.5 flex items-baseline justify-between gap-4 group"
            >
              <span className={`px-display text-base md:text-lg font-semibold transition-colors ${hovered === i ? 'text-[var(--px-accent)]' : 'text-[var(--px-ink)]'}`}>{c.title}</span>
              <span className="px-data text-[10px] tracking-[0.16em] text-[var(--px-ink-soft)] shrink-0">
                {countLabel(c, t)}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="relative bg-[var(--px-ink)] overflow-hidden hidden lg:block">
        <img src={resolveImageUrl(cat.featured?.img)} alt={cat.title} className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <span className="absolute inset-0 px-scrim-base" />
        <div className="absolute inset-x-0 bottom-0 p-8">
          <p className="px-data text-[9px] tracking-[0.25em] uppercase text-white/55">{cat.title}</p>
          <p className="px-display text-2xl font-semibold text-white mt-2">{cat.featured?.name}</p>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-4">
            {cat.items.map(item => (
              <Link key={item.slug} to={item.url || `/materials/${cat.slug}/${item.slug}`} className="grade-name inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition-colors">
                {item.name}
                <StockDot on={item.in_stock} label={item.stock} bright />
              </Link>
            ))}
          </div>
          <Link to={cat.url || `/materials/${cat.slug}`} data-testid="mega-menu-browse-category" className="px-data inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white mt-5 border-b border-white/40 pb-1">
            {t('nav.browseAll')} <ArrowRight className="w-3 h-3 rtl-flip" />
          </Link>
        </div>
      </div>
    </div>
    <QuickBar links={links} lang={lang} t={t} />
  </div>
);

/* 5 — Spotlight: featured grade panel left, family pills and grade grid right */
export const MegaSpotlight = ({ cats, cat, setCat, hovered, links, lang, t }) => (
  <div className="flex flex-col">
    <div className={`grid grid-cols-1 lg:grid-cols-[340px_1fr] ${PANEL}`}>
      <div className="relative bg-[var(--px-ink)] overflow-hidden hidden lg:block">
        <img src={resolveImageUrl(cat.featured?.img)} alt={cat.featured?.name} className="absolute inset-0 w-full h-full object-cover opacity-75" />
        <span className="absolute inset-0 px-scrim-base" />
        <div className="absolute inset-x-0 bottom-0 p-7">
          <span className="px-data px-pill bg-[var(--px-accent)] text-white text-[9px] tracking-[0.16em] uppercase px-2.5 py-1">{t('nav.mostAsked')}</span>
          <p className="px-display text-2xl font-semibold text-white mt-4 leading-tight">{cat.featured?.name}</p>
          <Link to={cat.featured?.url || `/materials/${cat.slug}/${cat.featured?.slug}`} data-testid="mega-featured-product" className="px-data inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white mt-4 border-b border-white/40 pb-1">
            {t('nav.viewDatasheet')} <ArrowRight className="w-3 h-3 rtl-flip" />
          </Link>
        </div>
      </div>
      <div className="p-8 md:p-10 flex flex-col">
        <div className="flex flex-wrap gap-2 mb-6">
          {cats.map((c, i) => (
            <button
              key={c.slug}
              onMouseEnter={() => setCat(i)}
              data-testid={`mega-cat-${i}`}
              className={`px-data px-pill border px-hair px-4 py-2 text-[10px] uppercase tracking-[0.16em] transition-colors duration-400 ${hovered === i ? 'bg-[var(--px-ink)] text-white border-[var(--px-ink)]' : 'text-[var(--px-ink-soft)] hover:text-[var(--px-ink)]'}`}
            >
              {c.title}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-1 content-start">
          {cat.items.map(item => (
            <Link key={item.slug} to={item.url || `/materials/${cat.slug}/${item.slug}`} className="grade-name flex items-center gap-2 text-sm text-[var(--px-ink-soft)] hover:text-[var(--px-accent)] py-1 transition-colors">
              <span className="truncate">{item.group ? `${item.group.split(' · ')[1] || item.group} — ${item.short || item.name}` : item.name}</span>
              <StockDot on={item.in_stock} label={item.stock} />
            </Link>
          ))}
        </div>
      </div>
    </div>
    <QuickBar links={links} lang={lang} t={t} />
  </div>
);

/* 6 — List: the dense, image-free full index — family plus every grade under it */
export const MegaList = ({ cats, links, lang, t }) => (
  <div>
    <div className="p-7 md:p-8">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-x-8 gap-y-7">
        {cats.map((c, i) => (
          <div key={c.slug} data-testid={`mega-cat-${i}`}>
            <Link to={c.url || `/materials/${c.slug}`} className="group flex items-baseline justify-between gap-3 border-b px-hair pb-2 mb-2">
              <span className="px-display text-sm font-semibold text-[var(--px-ink)] group-hover:text-[var(--px-accent)] transition-colors">{c.title}</span>
              <span className="px-data text-[10px] tracking-[0.14em] text-[var(--px-ink-soft)] shrink-0">{c.items.length}</span>
            </Link>
            <div className="space-y-0.5">
              {grouped(c.items).map(g => (
                <div key={g.label || 'all'}>
                  {g.label && <GroupHead label={g.label} />}
                  {g.items.map(item => (
                    <Link key={item.slug} to={item.url || `/materials/${c.slug}/${item.slug}`} className="grade-name flex items-center gap-2 text-[13px] text-[var(--px-ink-soft)] hover:text-[var(--px-accent)] transition-colors">
                      <span className="truncate">{item.short || item.name}</span>
                      <StockDot on={item.in_stock} label={item.stock} />
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
    <QuickBar links={links} lang={lang} t={t} />
  </div>
);

export const MEGA_RENDERERS = {
  rail: MegaRail,
  columns: MegaColumns,
  cards: MegaCards,
  editorial: MegaEditorial,
  spotlight: MegaSpotlight,
  list: MegaList,
};
